import { type AxiosError } from 'axios'
import useSWR, { type SWRResponse } from 'swr'

import {
  type CachedHierarchyEntryResponseDto,
  StoryblokCacheProxyControllerApiFactory,
} from '@redteclab/api/clients/bully'

import { useGlooApi } from '../../../../../../../api'
import { apmCaptureError, apmSetCustomContext } from '../../../../../../../apm'
import { useGlobalConfigContext } from '../../../../../../../global-config'

const getCategorySwrKey = (
  storyblokCategorySlug: string | null | undefined,
  shouldFetchData?: boolean,
): string | null => {
  if (!storyblokCategorySlug || !shouldFetchData) {
    return null
  }

  return `category-hierarchy-${storyblokCategorySlug}`
}

export const useCategoryHierarchyNavigationSwr = ({
  initialData,
  shouldFetchData,
  storyblokCategorySlug,
}: {
  initialData?: CachedHierarchyEntryResponseDto
  shouldFetchData: boolean
  storyblokCategorySlug: string
}): SWRResponse<CachedHierarchyEntryResponseDto | null, AxiosError> => {
  const client = useGlooApi(StoryblokCacheProxyControllerApiFactory)
  const { language, tenant } = useGlobalConfigContext()

  const fetcher = async (): Promise<CachedHierarchyEntryResponseDto | null> => {
    if (storyblokCategorySlug === initialData?.fullSlug) {
      return initialData
    }

    const categoryHierarchyResponse =
      await client.cachedHierarchydStoryControllerGetEntry({
        encodedFullSlug: storyblokCategorySlug,
        isDraft: false,
        language,
        tenant,
      })

    return categoryHierarchyResponse.data
  }

  return useSWR(
    getCategorySwrKey(storyblokCategorySlug, shouldFetchData),
    fetcher,
    {
      onError: (error: AxiosError) => {
        apmSetCustomContext({ slug: storyblokCategorySlug })
        apmCaptureError(error)
      },
    },
  )
}
